// Suspense hooks that load an avatar model: VRM (per-instance) or plain glTF.
import { useLayoutEffect, useRef, useState } from 'react';
import { useGLTF } from '@react-three/drei';
import type * as THREE from 'three';
import type { VRM } from '@pixiv/three-vrm';
import { isVrmUrl } from './vrmFormat';
import { disposeVrm, loadVrmAvatar, type VrmInstance } from './loadVrm';

interface Entry {
  url: string;
  promise: Promise<void>;
  result?: VrmInstance;
  error?: unknown;
  owner: object | null;
  timer?: ReturnType<typeof setTimeout>;
}
// A VRM instance (spring bones, expressions, first-person meshes) is bound to
// one scene graph, so every mounted avatar needs its own. A loading instance
// waits here, keyed by URL (stable across Suspense retries, unlike useId);
// the component that commits first adopts it and removes it from the map, so
// the next avatar with the same URL starts a fresh load.
const pending = new Map<string, Entry>();

function startLoad(url: string): Entry {
  const e: Entry = { url, owner: null, promise: Promise.resolve() };
  e.promise = loadVrmAvatar(url).then((r) => { e.result = r; }, (err) => { e.error = err; });
  pending.set(url, e);
  return e;
}

/** Load a VRM avatar instance for this component (suspends while loading). */
export function useVrmModel(url: string): VrmInstance {
  const [, reload] = useState(0);
  const token = useRef<object>({});
  const owned = useRef<Entry | null>(null);
  const e = owned.current?.url === url ? owned.current : (pending.get(url) ?? startLoad(url));
  if (e.error) {
    if (pending.get(url) === e) pending.delete(url);
    throw e.error;
  }
  if (!e.result) throw e.promise;
  useLayoutEffect(() => {
    if (e.owner && e.owner !== token.current) { reload((n) => n + 1); return; } // another avatar took it
    e.owner = token.current;
    owned.current = e;
    if (e.timer) clearTimeout(e.timer);
    if (pending.get(url) === e) pending.delete(url);
    return () => {
      e.owner = null;
      owned.current = null;
      // StrictMode / quick remounts re-adopt within this delay.
      e.timer = setTimeout(() => { if (!e.owner && e.result) void disposeVrm(e.result); }, 1500);
    };
  }, [e, url]);
  return e.result;
}

export interface AvatarModel {
  scene: THREE.Object3D;
  animations: THREE.AnimationClip[];
  /** Present for VRM avatars only. */
  vrm?: VRM;
}

/**
 * The avatar renderer's model loader. glTF/GLB go through drei's shared cache;
 * .vrm files get their own VRM instance. Callers must remount (key) when an
 * avatar switches between the two kinds, so the hook order stays stable.
 */
export function useAvatarModel(url: string): AvatarModel {
  /* eslint-disable react-hooks/rules-of-hooks */
  if (isVrmUrl(url)) return useVrmModel(url);
  return useGLTF(url) as unknown as AvatarModel;
  /* eslint-enable react-hooks/rules-of-hooks */
}
